import React from "react";
import PropTypes from "prop-types";
import Text from "@/bit/components/components.text";
import getColor from "@gcloud-npm/utils.enums.colors";
import COMPONENTS_MAP from "../../componentsImports.js";

function toStyleObject(additionalProps) {
  if (
    additionalProps &&
    typeof additionalProps === "object" &&
    !Array.isArray(additionalProps)
  ) {
    return additionalProps;
  }

  if (typeof additionalProps === "string" && additionalProps.trim()) {
    try {
      const parsed = JSON.parse(additionalProps);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {}
  }

  return {};
}

function normalizeStyles(obj) {
  const normalized = {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const camelKey = key.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    normalized[camelKey] = obj[key];
  }

  return normalized;
}
/**
 * Componente que permite crear un componente mezclando unos ya existentes.
 */
const CmsContainer = (props) => {
  const {
    cmsHandler,
    blockName,
    alias,
    "layout-props": layoutProps,
    "additional-props": additionalProps,
    "use-container-class": useContainerClass = true,
    "cms-ml-content-1": contentList,
    CmsAddElement,
    CmsEditableGroupIcons,
  } = props;

  if (!contentList || !layoutProps || !layoutProps.display) {
    return null;
  }

  let styles = {};
  const color = props.color || "white";
  const backgroundColor = getColor(color);

  switch (layoutProps.display) {
    case "flex":
      styles = {
        display: layoutProps.display,
        ...layoutProps["flex-props"],
      };
      break;

    case "grid":
      styles = {
        display: layoutProps.display,
        ...layoutProps["grid-props"],
      };
      break;

    case "inline":
    case "inline-block":
    case "none":
    case "block":
    default:
      styles = {
        display: layoutProps.display,
      };
      break;
  }

  return (
    <section
      id={blockName || ""}
      className={`cms-container${useContainerClass ? " container" : ""}${
        cmsHandler ? " cms-container--editing" : ""
      }`}
      style={{ backgroundColor: backgroundColor }}
    >
      {CmsEditableGroupIcons && (
        <CmsEditableGroupIcons
          noCustomLink
          elementProps={{
            ...props,
            editName: "cms-block.cms-container",
          }}
          deleteProps={{}}
          visibleTools={{
            hiring: false,
            edit: true,
            deleteItem: false,
          }}
          cmsHandler={cmsHandler}
        />
      )}
      {CmsAddElement && (
        <CmsAddElement
          addProps={{
            index: "start",
            contentId: blockName,
            itemIdDotNotation: `${blockName}.cms-ml-content-1`,
            layout: "vertical",
            openChooseModalFlag: true,
          }}
          cmsHandler={cmsHandler}
        />
      )}
      <div
        className={`cms-container--list`}
        style={normalizeStyles({
          ...styles,
          ...toStyleObject(additionalProps),
        })}
      >
        {contentList && Object.keys(contentList).length > 0 ? (
          Object.keys(contentList).map((cmp, index) => {
            const Component = COMPONENTS_MAP[cmp.replace(/-\d+$/, "")];

            if (!Component) {
              return null;
            }

            return (
              <div key={"cms-content-" + cmp} className="cms-container--item">
                {CmsEditableGroupIcons && (
                  <CmsEditableGroupIcons
                    elementProps={{}}
                    deleteProps={{
                      index,
                      itemIdDotNotation: `${blockName}.cms-ml-content-1`,
                      alias,
                      componentToDelete: cmp,
                    }}
                    visibleTools={{
                      hiring: false,
                      edit: false,
                      deleteItem: true,
                    }}
                    cmsHandler={cmsHandler}
                  />
                )}
                <Component
                  blockName={`${blockName}.cms-ml-content-1.${cmp}`}
                  alias={alias}
                  {...contentList[cmp]}
                  cmsHandler={cmsHandler}
                  CmsAddElement={CmsAddElement}
                  CmsEditableGroupIcons={CmsEditableGroupIcons}
                />
              </div>
            );
          })
        ) : (
          <div className="empty-content">
            <Text
              align="center"
              color="grey6"
              text="Actualmente no existe contenido en este contenedor, pruebe a abrir
            el modo edicion y añadir un componente"
            />
          </div>
        )}
      </div>
      {CmsAddElement && (
        <CmsAddElement
          addProps={{
            index: "end",
            contentId: blockName,
            itemIdDotNotation: `${blockName}.cms-ml-content-1`,
            layout: "vertical",
            openChooseModalFlag: true,
          }}
          cmsHandler={cmsHandler}
        />
      )}
      <style jsx>{`.cms-container {
  background-color: #fff;
  margin-bottom: 1rem !important;

  &--editing {
    border: 2px dashed #ccc;
    border-radius: 8px;
  }

  .empty-content {
    padding: 2rem;
    background-color: #eeeeeeb0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    margin: auto;
  }

  &--item {
    position: relative;
  }

  &--list {
    display: flex;
    position: relative;
    height: 100%;
    &.direction-column {
      flex-direction: column;
    }
  }
}
`}</style>
    </section>
  );
};

CmsContainer.propTypes = {
  /**
   * {
   *  "info": "Posicionamiento de componentes dentro del contenedor",
   *  "kind": "both",
   *  "beautifulName": "Propiedades del Contenedor"
   * }
   */
  "layout-props": PropTypes.object,
  /**
   * {
   *  "info": "Estilos adicionales que se le quiera dar al contenedor, como margin, padding, etc.",
   *  "kind": "both",
   *  "beautifulName": "Estilos adicionales"
   * }
   */
  "additional-props": PropTypes.object,
  /**
   * {
   *  "info": "Añade la clase container al contenedor. Desactívalo si es un contenedor anidado dentro de otro.",
   *  "kind": "both",
   *  "beautifulName": "Usar clase container"
   * }
   */
  "use-container-class": PropTypes.bool,
  /**
   * {
   *  "info": "Color de fondo",
   *  "kind": "both",
   *  "beautifulName": "Color de fondo"
   * }
   */
  color: PropTypes.string,
};

CmsContainer.defaultProps = {
  "layout-props": { display: "block" },
  "additional-props": {},
  "use-container-class": true,
  color: "white",
};

export default CmsContainer;
